import React, { useState, useEffect } from 'react';
import './Transitions.css';
import { DEVICES } from '../../constants/breakpoints';

const Transitions = ({
  selectedElement,
  updateElementStyles,
  selectedDevice
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [transitions, setTransitions] = useState({
    all: {
      duration: 0.3,
      delay: 0,
      timing: 'ease'
    },
    transform: {
      enabled: false,
      duration: 0.3,
      delay: 0,
      timing: 'ease'
    },
    opacity: {
      enabled: false,
      duration: 0.3,
      delay: 0,
      timing: 'ease'
    },
    background: {
      enabled: false,
      duration: 0.3,
      delay: 0,
      timing: 'ease'
    },
    colors: {
      enabled: false,
      duration: 0.3,
      delay: 0,
      timing: 'ease'
    }
  });
  const [expandedTransitions, setExpandedTransitions] = useState({
    all: false,
    transform: false,
    opacity: false,
    background: false,
    colors: false
  });
  const [appliedTransitions, setAppliedTransitions] = useState({
    transform: false,
    opacity: false,
    background: false,
    colors: false
  });

  const timingFunctions = [
    'linear',
    'ease',
    'ease-in',
    'ease-out',
    'ease-in-out'
  ];

  useEffect(() => {
    if (selectedElement?.styles) {
      const newTransitions = {
        all: {
          duration: 0.3,
          delay: 0,
          timing: 'ease'
        },
        transform: {
          enabled: false,
          duration: 0.3,
          delay: 0,
          timing: 'ease'
        },
        opacity: {
          enabled: false,
          duration: 0.3,
          delay: 0,
          timing: 'ease'
        },
        background: {
          enabled: false,
          duration: 0.3,
          delay: 0,
          timing: 'ease'
        },
        colors: {
          enabled: false,
          duration: 0.3,
          delay: 0,
          timing: 'ease'
        }
      };
      const newAppliedTransitions = {
        transform: false,
        opacity: false,
        background: false,
        colors: false
      };

      // Helper function to parse transition values
      const parseTransitionValues = (transitionStr, property) => {
        if (!transitionStr) return null;
        const parts = transitionStr.split(',').map(t => t.trim());
        const propertyTransition = parts.find(t => t.startsWith(property));
        
        if (propertyTransition) {
          const [, duration, timing, delay] = propertyTransition.split(' ');
          return {
            duration: parseFloat(duration) || 0.3,
            timing: timing || 'ease',
            delay: parseFloat(delay) || 0,
            enabled: true
          };
        }
        return null;
      };

      // Get transition string based on device cascade
      let transitionString;
      if (selectedDevice === DEVICES.MOBILE) {
        transitionString = selectedElement.styles?.[DEVICES.MOBILE]?.transition ||
                          selectedElement.styles?.[DEVICES.TABLET]?.transition ||
                          selectedElement.styles?.[DEVICES.DESKTOP]?.transition ||
                          selectedElement.styles?.transition;
      } else if (selectedDevice === DEVICES.TABLET) {
        transitionString = selectedElement.styles?.[DEVICES.TABLET]?.transition ||
                          selectedElement.styles?.[DEVICES.DESKTOP]?.transition ||
                          selectedElement.styles?.transition;
      } else {
        transitionString = selectedElement.styles?.[DEVICES.DESKTOP]?.transition ||
                          selectedElement.styles?.transition;
      }

      if (transitionString) {
        // Update values for each property
        ['transform', 'opacity', 'background', 'colors'].forEach(property => {
          const values = parseTransitionValues(transitionString, property);
          if (values) {
            newTransitions[property] = values;
            newAppliedTransitions[property] = true;
          }
        });

        // Handle 'all' property
        const allValues = parseTransitionValues(transitionString, 'all');
        if (allValues) {
          newTransitions.all = {
            duration: allValues.duration,
            delay: allValues.delay,
            timing: allValues.timing
          };
        }
      }

      setTransitions(newTransitions);
      setAppliedTransitions(newAppliedTransitions);
    }
  }, [selectedElement, selectedDevice]);

  const mergeTransitions = (...transitionStrings) => {
    const mergedTransitions = new Map();
    
    transitionStrings.filter(Boolean).forEach(transitionString => {
      const parts = transitionString.split(',').map(t => t.trim());
      parts.forEach(part => {
        const [property, ...values] = part.split(' ');
        if (!mergedTransitions.has(property)) {
          mergedTransitions.set(property, values.join(' '));
        }
      });
    });
    
    return Array.from(mergedTransitions.entries())
      .map(([property, values]) => `${property} ${values}`)
      .join(', ');
  };

  const handleTransitionChange = (property, field, value) => {
    const newTransitions = {
      ...transitions,
      [property]: {
        ...transitions[property],
        [field]: value
      }
    };
    setTransitions(newTransitions);
    updateTransitions(newTransitions);
    
    setAppliedTransitions(prev => ({
      ...prev,
      [property]: true
    }));
    
    setExpandedTransitions(prev => ({
      ...prev,
      [property]: true
    }));
  };

  const toggleTransitionProperty = (property) => {
    const newTransitions = {
      ...transitions,
      [property]: {
        ...transitions[property],
        enabled: !transitions[property].enabled
      }
    };
    setTransitions(newTransitions);
    updateTransitions(newTransitions);
  };

  const updateTransitions = (newTransitions) => {
    const transitionParts = [];

    // Add individual property transitions first
    Object.entries(newTransitions).forEach(([property, settings]) => {
      if (property !== 'all' && settings.enabled) {
        transitionParts.push(
          `${property} ${settings.duration}s ${settings.timing} ${settings.delay}s`
        );
      }
    });

    // Add 'all' transition if needed
    if (
      (transitionParts.length === 0 && 
       (newTransitions.all.duration !== 0.3 || 
        newTransitions.all.delay !== 0 || 
        newTransitions.all.timing !== 'ease')) ||
      (newTransitions.all.duration !== 0.3 || 
       newTransitions.all.delay !== 0 || 
       newTransitions.all.timing !== 'ease')
    ) {
      transitionParts.unshift(
        `all ${newTransitions.all.duration}s ${newTransitions.all.timing} ${newTransitions.all.delay}s`
      );
    }

    const transitionString = transitionParts.length > 0 ? transitionParts.join(', ') : 'none';

    updateElementStyles(
      selectedElement.id,
      "transition",
      transitionString,
      null,
      selectedDevice
    );
  };

  const toggleTransition = (property) => {
    setExpandedTransitions(prev => ({
      ...prev,
      [property]: !prev[property]
    }));

    if (!expandedTransitions[property] && property !== 'all') {
      setAppliedTransitions(prev => ({
        ...prev,
        [property]: true
      }));
      
      const newTransitions = {
        ...transitions,
        [property]: {
          ...transitions[property],
          enabled: true
        }
      };
      setTransitions(newTransitions);
      updateTransitions(newTransitions);
    }
  };

  const handleCheckboxClick = (e, property) => {
    e.stopPropagation();
    toggleTransitionProperty(property);
    if (!transitions[property].enabled) {
      setExpandedTransitions(prev => ({
        ...prev,
        [property]: true
      }));
    }
  };

  const renderTransitionControls = (property, settings) => (
    <div className="transition-content">
      <div className="transition-control">
        <label>Duration</label>
        <div className="slider-input-group">
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={settings.duration}
            onChange={(e) => handleTransitionChange(property, 'duration', parseFloat(e.target.value))}
          />
          <input
            type="number"
            min="0"
            max="2"
            step="0.1"
            value={settings.duration}
            onChange={(e) => handleTransitionChange(property, 'duration', parseFloat(e.target.value) || 0)}
          />
          <span className="unit">s</span>
        </div>
      </div>
      <div className="transition-control">
        <label>Delay</label>
        <div className="slider-input-group">
          <input
            type="range"
            min="0"
            max="2"
            step="0.1"
            value={settings.delay}
            onChange={(e) => handleTransitionChange(property, 'delay', parseFloat(e.target.value))}
          />
          <input
            type="number"
            min="0"
            max="2"
            step="0.1"
            value={settings.delay}
            onChange={(e) => handleTransitionChange(property, 'delay', parseFloat(e.target.value) || 0)}
          />
          <span className="unit">s</span>
        </div>
      </div>
      <div className="transition-control">
        <label>Timing</label>
        <select
          value={settings.timing}
          onChange={(e) => handleTransitionChange(property, 'timing', e.target.value)}
          className="timing-select"
        >
          {timingFunctions.map(timing => (
            <option key={timing} value={timing}>
              {timing}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const handleAddTransition = (property) => {
    const newTransitions = {
      ...transitions,
      [property]: {
        ...transitions[property],
        enabled: true
      }
    };
    setTransitions(newTransitions);
    setAppliedTransitions(prev => ({
      ...prev,
      [property]: true
    }));
    updateTransitions(newTransitions);
  };

  const handleRemoveTransition = (property) => {
    const newTransitions = {
      ...transitions,
      [property]: {
        ...transitions[property],
        enabled: false
      }
    };
    setTransitions(newTransitions);
    setAppliedTransitions(prev => ({
      ...prev,
      [property]: false
    }));
    updateTransitions(newTransitions);
  };

  return (
    <div className="transitions-section">
      <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="section-title">
          <i className="fas fa-clock"></i>
          <span>Transitions</span>
        </div>
        <div className="section-controls">
          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
        </div>
      </div>

      {isExpanded && (
        <div className="transitions-controls">
          {/* All Properties Section */}
          <div className="transition-item">
            <div 
              className="transition-header"
              onClick={() => toggleTransition('all')}
            >
              <span className="transition-title">All Properties</span>
              <i className={`fas fa-chevron-${expandedTransitions.all ? 'down' : 'right'}`}></i>
            </div>
            {expandedTransitions.all && renderTransitionControls('all', transitions.all)}
          </div>

          {/* Individual Property Sections */}
          {Object.entries({
            transform: 'Transform',
            opacity: 'Opacity',
            background: 'Background',
            colors: 'Colors'
          }).map(([property, label]) => (
            <div key={property} className="transition-item">
              <div 
                className="transition-header"
                onClick={() => toggleTransition(property)}
              >
                <div className="transition-title-group">
                  <span className="transition-title">{label}</span>
                  {!appliedTransitions[property] ? (
                    <button 
                      className="add-transition-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddTransition(property);
                      }}
                      title={`Add ${label} Transition`}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  ) : (
                    <button 
                      className="remove-transition-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveTransition(property);
                      }}
                      title={`Remove ${label} Transition`}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                <i className={`fas fa-chevron-${expandedTransitions[property] ? 'down' : 'right'}`}></i>
              </div>
              {expandedTransitions[property] && 
                renderTransitionControls(property, transitions[property])
              }
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Transitions; 